import { useEffect, useRef, useState } from 'react';

import {
  Box,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { X } from 'lucide-react';

import { AddressApi } from '@/api';
import { MunicipalityType, IAddressForm } from '@/models/address';
import { useToastStore } from '@/store';

interface MunicipalityTypeItemProps {
  value?: IAddressForm;
  updateCurrentAddress: (newAddress: IAddressForm | null) => void;
  disabled?: boolean;
  /**
   * @description If true, shows a clear (X) button when a value is selected.
   */
  clearable?: boolean;
}

export const MunicipalityTypeItem = (props: MunicipalityTypeItemProps) => {
  const { value, updateCurrentAddress, disabled, clearable = false } = props;
  const { updateToast } = useToastStore();
  const [pendingUpdate, setPendingUpdate] = useState<NodeJS.Timeout | null>(
    null,
  );
  const selectRef = useRef<HTMLDivElement>(null);
  const [selectWidth, setSelectWidth] = useState<number | undefined>(undefined);

  const { addressNumber, completeStreetName, zipcode } = value || {};

  const enableField = !!addressNumber || !!completeStreetName || !!zipcode;

  // Track the select width for the menu
  useEffect(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.offsetWidth);
    }
  }, [selectRef.current?.offsetWidth]);

  // Cleanup pending debounced calls on unmount
  useEffect(() => {
    return () => {
      if (pendingUpdate) {
        clearTimeout(pendingUpdate);
      }
    };
  }, [pendingUpdate]);

  const updateAddressAPI = async (updatedAddress: IAddressForm) => {
    try {
      await AddressApi.updateAddress(updatedAddress);
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message || 'Failed to update address municipality type.',
      });
    }
  };

  const handleChange = (newValue: string | number) => {
    if (!value) return;

    const updatedAddress: IAddressForm = {
      ...value,
      municipalityType: newValue
        ? (Number(newValue) as MunicipalityType)
        : undefined,
    };

    // Update local state immediately (optimistic)
    updateCurrentAddress(updatedAddress);

    // If address has _id, trigger debounced API update
    if (value._id) {
      // Clear any pending update
      if (pendingUpdate) {
        clearTimeout(pendingUpdate);
      }

      // Schedule new update with 500ms debounce
      const timeout = setTimeout(() => {
        updateAddressAPI(updatedAddress);
      }, 500);

      setPendingUpdate(timeout);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleChange('');
  };

  const showClearButton =
    clearable &&
    value?.municipalityType !== undefined &&
    value?.municipalityType !== null &&
    !disabled &&
    enableField;

  return (
    <Stack sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          opacity: disabled ? 0.5 : 1,
          width: '100%',
        }}
      >
        <InputLabel sx={{ mt: 1 }} disabled={!enableField}>
          City, Town, Village
        </InputLabel>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Select
            ref={selectRef}
            value={value?.municipalityType ?? ''}
            onChange={(e) => handleChange(Number(e.target.value))}
            disabled={!enableField || disabled}
            displayEmpty
            sx={{
              width: '100%',
              minWidth: '120px', // Minimum width to display values
              maxWidth: '100%', // Match parent width if larger
              '& .MuiSelect-select': {
                paddingRight: showClearButton ? '70px' : '32px',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: selectWidth ? `${selectWidth}px` : 'auto',
                  minWidth: selectWidth ? `${selectWidth}px` : 'auto',
                },
              },
              sx: {
                '& .MuiMenuItem-root': {
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                },
              },
            }}
          >
            <MenuItem value={MunicipalityType.CITY}>City</MenuItem>
            <MenuItem value={MunicipalityType.TOWN}>Town</MenuItem>
            <MenuItem value={MunicipalityType.VILLAGE}>Village</MenuItem>
          </Select>
          {showClearButton && (
            <IconButton
              size="small"
              onClick={handleClear}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              sx={{
                position: 'absolute',
                right: '32px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px',
                '&:hover': {
                  color: '#EE3E5C',
                },
              }}
              aria-label="Clear municipality type"
            >
              <X size={16} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Stack>
  );
};
